import * as React from 'react';
import {
  YMapControls,
  YMapDefaultFeaturesLayer,
  YMapDefaultSchemeLayer,
  YMapFeature,
  YMapMarker,
  YMapProvider,
  YMapZoomControl,
} from '@cian/frontend-ymaps-components';
import type { YMaps } from '@cian/frontend-ymaps-components/es/types/ymaps';

import { ICoordinates } from '../../types/newbuildingsCompare';
import {
  IFutureInfrastructurePoi,
  IFutureRoad,
  IInfrastructurePoi,
  ITaPoi,
  TTaCategory,
} from '../../types/infrastructure';
import { FUTURE_ROAD_LINE_HEX } from './constants';
import { MAP_MARGIN, TMapPlatform } from '../../constants/map';
import { MapErrorBoundary } from '../MapErrorBoundary';
import { MainPin } from './parts/MainPin';
import { FuturePin } from './parts/FuturePin';
import { InfrastructurePin } from './parts/InfrastructurePin';
import { TaPin } from './parts/TaPin';
import { YMapWithDefaultLocation } from '../YMapWithDefaultLocation';
import { toLngLat } from '../../utils/map/getMapLocation';
import { useApplicationContext } from '../../utils/applicationContext';
import * as styles from './styles.css';

/** Зум, на котором виден район вокруг ЖК. */
const DISTRICT_ZOOM = 15;

// Стили рисуются движком карты, CSS-переменные там не резолвятся — цвета задаём хексом (--accent-main-primary).
const POLYGON_STYLE: YMaps.DrawingStyle = {
  fill: '#005ede',
  fillOpacity: 0.12,
  stroke: [{ color: '#005ede', width: 2 }],
};
const FUTURE_ROAD_STYLE: YMaps.DrawingStyle = {
  stroke: [{ color: FUTURE_ROAD_LINE_HEX, width: 4 }],
};

export interface IDistrictInfrastructureMapProps {
  apiKey: string;
  center: ICoordinates;
  /** Строящиеся объекты — рисуются в режиме «Будущее района». */
  futurePois?: IFutureInfrastructurePoi[];
  /** Будущие дороги — полилиния и пин; рисуются в режиме «Будущее района». */
  futureRoads?: IFutureRoad[];
  /** Положение карты вместо дефолтного (центр ЖК, зум района). */
  locationOverride?: YMaps.YMapLocationRequest;
  /** Поля карты вместо дефолтных — под панель, перекрывающую карту. */
  marginOverride?: YMaps.Margin;
  platform?: TMapPlatform;
  pois: IInfrastructurePoi[];
  /** Полигон ЖК — рисуется, если бэк его прислал. */
  polygon?: ICoordinates[];
  /** Точки выбранной категории транспортной доступности. */
  taPois?: { category: TTaCategory; items: ITaPoi[] } | null;
  title: string;
}

/** Карта района: пин ЖК в центре и точки инфраструктуры вокруг. */
export function DistrictInfrastructureMap({
  apiKey,
  center,
  futurePois,
  futureRoads,
  locationOverride,
  marginOverride,
  platform = 'desktop',
  pois,
  polygon,
  taPois,
  title,
}: IDistrictInfrastructureMapProps): React.JSX.Element {
  const { logger } = useApplicationContext();

  const handleMapError = React.useCallback(
    (error: unknown) => {
      const normalizedError: Error = error instanceof Error ? error : new Error(String(error));

      logger.error(normalizedError, { degradation: 'Карта района в сравнении ЖК' });
    },
    [logger],
  );

  // Объект стабилен между рендерами: карта переезжает только при смене центра или override,
  // а не при каждом клике по чипсу фильтра.
  const location = React.useMemo<YMaps.YMapLocationRequest>(
    () => locationOverride ?? { center: toLngLat(center), zoom: DISTRICT_ZOOM },
    [locationOverride, center],
  );

  return (
    <div className={styles['container']}>
      <MapErrorBoundary onError={handleMapError}>
        <YMapProvider apiKey={apiKey} lang="ru_RU" onError={handleMapError}>
          <YMapWithDefaultLocation
            location={location}
            margin={marginOverride ?? MAP_MARGIN[platform]}
            zoomRounding="smooth"
          >
            <YMapDefaultSchemeLayer />
            <YMapDefaultFeaturesLayer />
            {polygon && polygon.length > 2 && (
              <YMapFeature geometry={{ type: 'Polygon', coordinates: [polygon.map(toLngLat)] }} style={POLYGON_STYLE} />
            )}
            {futureRoads?.map(road => (
              <YMapFeature
                key={`${road.id}-line`}
                geometry={{ type: 'LineString', coordinates: road.line.map(toLngLat) }}
                style={FUTURE_ROAD_STYLE}
              />
            ))}
            <YMapMarker blockEvents coordinates={toLngLat(center)}>
              <MainPin title={title} />
            </YMapMarker>
            {pois.map(poi => (
              <YMapMarker blockEvents key={poi.id} coordinates={toLngLat(poi.coordinates)}>
                <InfrastructurePin poi={poi} />
              </YMapMarker>
            ))}
            {futurePois?.map(poi => (
              <YMapMarker blockEvents key={poi.id} coordinates={toLngLat(poi.coordinates)}>
                <FuturePin constructionDateText={poi.constructionDateText} type={poi.type} />
              </YMapMarker>
            ))}
            {futureRoads?.map(road => (
              <YMapMarker blockEvents key={road.id} coordinates={toLngLat(road.coordinates)}>
                <FuturePin constructionDateText={road.constructionDateText} />
              </YMapMarker>
            ))}
            {taPois?.items.map(poi => (
              <YMapMarker blockEvents key={poi.id} coordinates={toLngLat(poi.coordinates)}>
                <TaPin category={taPois.category} poi={poi} />
              </YMapMarker>
            ))}
            <YMapControls position="right">
              <YMapZoomControl />
            </YMapControls>
          </YMapWithDefaultLocation>
        </YMapProvider>
      </MapErrorBoundary>
    </div>
  );
}
